/* eslint-disable react-refresh/only-export-components -- context + hook co-located per React convention */
import {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
  useRef,
  type ReactNode,
  type JSX,
} from 'react';
import { silentRefresh } from '../api/client.ts';
import { NetworkError } from '../api/errors.ts';

export interface AuthState {
  accessToken: string | null;
  userId: string | null;
  role: string | null;
  practiceId: string | null;
}

export interface AuthContextValue extends AuthState {
  /**
   * False until the on-load silent-refresh attempt resolves. Consumers must
   * wait for this before deciding to redirect to /login - otherwise a full
   * page reload (deep link / refresh), which clears the in-memory token,
   * would bounce an authenticated user to login before the refresh cookie can
   * restore the session.
   */
  bootstrapped: boolean;
  /** Set when the on-load silent refresh failed on a dropped connection rather
   *  than a rejected/expired cookie - bootstrapped stays false so the shell
   *  offers a retry instead of bouncing an authenticated user to /login. */
  bootstrapError: 'network' | null;
  retryBootstrap: () => void;
  setTokens: (tokens: {
    accessToken: string;
    userId: string;
    role: string;
    practiceId: string | null;
  }) => void;
  clearTokens: () => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

const EMPTY: AuthState = {
  accessToken: null,
  userId: null,
  role: null,
  practiceId: null,
};

export function AuthProvider({ children }: { children: ReactNode }): JSX.Element {
  const [state, setState] = useState<AuthState>(EMPTY);
  const [bootstrapped, setBootstrapped] = useState(false);
  const [bootstrapError, setBootstrapError] = useState<'network' | null>(null);
  const attemptRef = useRef(0);

  // Fences any bootstrap attempt still in flight: without the bump its catch
  // still passes the attemptRef guard and writes bootstrapError over a session
  // this login just established (one unsolicited refresh).
  const setTokens = useCallback(
    (tokens: {
      accessToken: string;
      userId: string;
      role: string;
      practiceId: string | null;
    }) => {
      setState({
        accessToken: tokens.accessToken,
        userId: tokens.userId,
        role: tokens.role,
        practiceId: tokens.practiceId,
      });
      attemptRef.current++;
      setBootstrapped(true);
      setBootstrapError(null);
    },
    [],
  );

  const clearTokens = useCallback(() => setState(EMPTY), []);

  // Silent session restoration on load: the access token lives in memory only
  // (inv 7), so a reload/deep-link starts with no token. Attempt a refresh via
  // the httpOnly `hapmetrix_refresh` cookie; if it succeeds the session is
  // restored without any token ever touching web storage. attemptRef discards
  // a stale attempt's result if a newer one (retry) has since started.
  const attemptBootstrap = useCallback(() => {
    const attempt = ++attemptRef.current;
    setBootstrapError(null);
    void (async () => {
      try {
        // Funnel through the shared single-flight refresh so this never races
        // a query's own 401-refresh on the same rotating cookie.
        const data = await silentRefresh();
        if (attemptRef.current !== attempt) return;
        if (data) {
          setState({
            accessToken: data.accessToken,
            userId: data.id,
            role: data.role,
            practiceId: null,
          });
        }
        setBootstrapped(true);
      } catch (err) {
        if (attemptRef.current !== attempt) return;
        if (err instanceof NetworkError) setBootstrapError('network');
        else setBootstrapped(true);
      }
    })();
  }, []);

  useEffect(() => {
    attemptBootstrap();
  }, [attemptBootstrap]);

  useEffect(() => {
    if (!bootstrapError) return;
    window.addEventListener('online', attemptBootstrap);
    const id = setInterval(attemptBootstrap, 15_000);
    return () => {
      window.removeEventListener('online', attemptBootstrap);
      clearInterval(id);
    };
  }, [bootstrapError, attemptBootstrap]);

  return (
    <AuthContext.Provider
      value={{ ...state, bootstrapped, bootstrapError, retryBootstrap: attemptBootstrap, setTokens, clearTokens }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside AuthProvider');
  return ctx;
}
